import type {
  EditVehicleDocumentPromptResolve,
  VehicleDocumentNode,
} from '@/modules/vehicles'
import { EditVehicleDocumentModal } from '@/modules/vehicles/fleet'
import { useVehicleDocumentMutations } from '@/modules/vehicles/fleet/mutations.hooks'
import { getExpirationAndNotificationTime } from '@/modules/vehicles/list-filters'
import { useConfirm } from '@/utils/hooks'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'

/** Edit/delete handlers for the vehicle documents page. */
export function useVehicleDocumentHandlers() {
  const translations = useTranslate({
    delete: 'documents.prompts.delete-document',
    deleteConfirmation: [
      'documents.prompts.delete-document-confirmation',
      { name: '' },
    ],
  })

  const addPrompt = usePrompt()
  const confirm = useConfirm()

  const { patchDocument, deleteDocument } = useVehicleDocumentMutations()

  async function editDocument(document: VehicleDocumentNode) {
    const { data } = await addPrompt<EditVehicleDocumentPromptResolve | null>(
      resolve => (
        <EditVehicleDocumentModal
          vehicleDocument={document}
          onSubmit={resolve}
        />
      )
    )
    if (!data) return

    const { expirationDate, notificationTime } =
      getExpirationAndNotificationTime(data.form, data.hasNotification)

    await patchDocument({
      variables: {
        id: document.id,
        input: {
          name: data.form.name,
          description: data.form.description,
          expirationDate,
          notificationTime,
          responsibleUser: data.form.responsibleUser,
        },
      },
    })
  }

  async function removeDocument(document: VehicleDocumentNode) {
    const { data: answer } = await confirm(
      translations.deleteConfirmation({ name: document.name }),
      translations.delete
    )
    if (!answer) return

    await deleteDocument({ variables: { id: document.id } })
  }

  return { editDocument, removeDocument }
}
